

import {
	View,
	LabeledFieldView,
	createLabeledInputText,
	ButtonView,
	submitHandler,
	ListView,
	ListItemView
} from '@ckeditor/ckeditor5-ui';
import Collection from '@ckeditor/ckeditor5-utils/src/collection';
import Model from '@ckeditor/ckeditor5-ui/src/model';
import {FONT_FAMILY_NAMES, DEFAULT_FONTS} from './fontfamilies';
import "../../theme/style.css"
import FocusTracker from '@ckeditor/ckeditor5-utils/src/focustracker';
import FocusCycler from '@ckeditor/ckeditor5-ui/src/focuscycler';
import ViewCollection from '@ckeditor/ckeditor5-ui/src/viewcollection';
import KeystrokeHandler from '@ckeditor/ckeditor5-utils/src/keystrokehandler';

export default class FormView extends View {
	constructor( locale ) {
		super( locale );

		this.fontFamilyInputView = this._createInput( 'Search Font Family Name' );

		this.nameListView = new ListView(this.locale);
		this._createNameList();

		this.childViews = this.createCollection( [
			this.fontFamilyInputView,
			this.nameListView,
		] );

		this.setTemplate( {
			tag: 'form',
			attributes: {
				class: [ 'ck', 'obvio-custom-font-family-input-form'],
				tabindex: '-1'
			},
			children: this.childViews
		} );

		const inputField = this.fontFamilyInputView.fieldView;

		inputField.on('input', ()=>{
			const searchVal = inputField.element.value.trim();
			if( searchVal.length > 2 ){
				this._updateNameList(searchVal)
			}
			else{
				this._updateNameList('')
			}
		})

		this.focusTracker = new FocusTracker();
		this.keystrokes = new KeystrokeHandler();
		this._focusables = new ViewCollection();

		this._focusCycler = new FocusCycler( {
			focusables: this._focusables,
			focusTracker: this.focusTracker,
			keystrokeHandler: this.keystrokes,
			actions: {
				// Navigate form fields backwards using the Shift + Tab keystroke.
				focusPrevious: 'tab',

				// Navigate form fields forwards using the Tab key.
				focusNext: 'arrowdown'
			}
		} );

	}

	render() {
		super.render();

		// Submit the form when the user clicked the save button or pressed enter in the input.
		submitHandler( {
			view: this
		} );

		this._addKeyEvent();
	}

	focus() {
		this.childViews.first.focus();
	}

	_createInput( label ) {
		const labeledInput = new LabeledFieldView( this.locale, createLabeledInputText );
		labeledInput.label = label;

		labeledInput.extendTemplate({
			attributes: {
				class: [
					'obvio-font-family-name-input-field'
				],
				tabindex: '-1'
			}
		})

		return labeledInput;
	}

	_createNameList() {

		const items = getDropdownItemsDefinitions(DEFAULT_FONTS);
		this._updateListView(items);

		this.nameListView.extendTemplate( {
			attributes: {
				class: [
					'obvio-font-family-name-list'
				],

				tabindex: '-1'
			}
		} );
	}

	_updateNameList(filterKey) {

		const filtered = filterKey === '' ? DEFAULT_FONTS : filterFontNames(filterKey);

		const items = getDropdownItemsDefinitions(filtered);

		this.nameListView.items.clear();
		this._updateListView(items);
	}

	_updateListView(items) {
		const listView = this.nameListView;

		listView.items.bindTo(items).using( ({type, model}) =>{
			const listItemView = new ListItemView( this.locale );

			const buttonView = new ButtonView( this.locale );
			buttonView.bind( ...Object.keys( model ) ).to( model );
			buttonView.delegate( 'execute' ).to( listItemView );

			listItemView.children.add( buttonView );
			return listItemView;

		})

	}

	_addKeyEvent() {
		[ this.fontFamilyInputView, this.nameListView ]
			.forEach( v => {
				this._focusables.add( v );
				this.focusTracker.add( v.element );
			} );

		// Start listening for the keystrokes coming from #element.
		this.keystrokes.listenTo( this.element );

		const stopPropagation = data => data.stopPropagation();

		this.keystrokes.set( 'arrowright', stopPropagation );
		this.keystrokes.set( 'arrowleft', stopPropagation );
		this.keystrokes.set( 'arrowup', stopPropagation );
		this.keystrokes.set( 'arrowdown', stopPropagation );

		this.listenTo( this.fontFamilyInputView.element, 'selectstart', ( evt, domEvt ) => {
			domEvt.stopPropagation();
		}, { priority: 'high' } );


	}

}


function getDropdownItemsDefinitions( filterFontNames ) {
	const itemDefinitions = new Collection();

	for ( const name of filterFontNames ) {
		const definition = {
			type: 'button',
			model: new Model( {
				commandParam: name.toString(),
				label: name.toString(),
				class: 'obvio-fontfamily-name-item',
				labelStyle: `font-family: ${name};`,
				withText: true
			} )
		};

		// Add the item definition to the collection.
		itemDefinitions.add( definition );
	}

	return itemDefinitions;
}

function filterFontNames(key){
	return FONT_FAMILY_NAMES.filter(name=>name.toLowerCase().includes(key.toLowerCase()));
}
